import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Scissors, RefreshCw } from 'lucide-react';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import ErrorBoundary from '@/components/ErrorBoundary/ErrorBoundary';
import SalonBookingWizard, { useSalonFont, WizardService, WizardStaff, WizardSubmitPayload } from '@/components/SalonBooking/SalonBookingWizard';

/**
 * BookAppointment — the in-app (logged-in) appointment booking flow for a salon/spa/barber/clinic branch,
 * reached from the Branch Hub's "Book an appointment". Same wizard as the public widget, but authenticated:
 * the request attaches to the customer's account (identity captured by default).
 * Route: /customer/organizers/:organizerId/branches/:branchId/appointments
 */
type Phase = 'loading' | 'error' | 'ready';

const BookAppointment: React.FC = () => {
  const { organizerId, branchId } = useParams<{ organizerId: string; branchId: string }>();
  const navigate = useNavigate();
  useSalonFont();

  const [phase, setPhase] = useState<Phase>('loading');
  const [message, setMessage] = useState('');
  const [branchName, setBranchName] = useState('');
  const [currency, setCurrency] = useState<string | null>(null);
  const [services, setServices] = useState<WizardService[]>([]);
  const [staff, setStaff] = useState<WizardStaff[]>([]);

  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); }, []);

  useEffect(() => {
    if (!branchId) { setPhase('error'); setMessage('Salon not found.'); return; }
    let alive = true;
    (async () => {
      setPhase('loading');
      try {
        const res = await apiService.getBranchAppointmentInfo(branchId);
        if (!alive) return;
        const d = res?.data || {};
        setBranchName(d.branchName || 'Salon');
        setCurrency(d.currency || null);
        setServices((d.services || []) as WizardService[]);
        setStaff((d.staff || []) as WizardStaff[]);
        setPhase('ready');
      } catch (e) {
        if (!alive) return;
        setPhase('error'); setMessage(e instanceof Error ? e.message : 'Something went wrong.');
      }
    })();
    return () => { alive = false; };
  }, [branchId]);

  const loadSlots = async (staffId: string, date: string, serviceId?: string) => {
    const r = await apiService.getBranchAppointmentSlots(branchId!, staffId, date, serviceId);
    return (r?.data?.slots || []) as string[];
  };
  const submit = async (p: WizardSubmitPayload) => {
    const r = await apiService.createBranchAppointment(branchId!, { serviceId: p.serviceId, staffId: p.staffId, date: p.date, time: p.time, name: p.name, phone: p.phone, notes: p.notes });
    const d = r?.data || {};
    return { time: d.time, date: d.date, serviceName: d.serviceName ?? null, staffName: d.staffName ?? null };
  };

  const joinWaitlist = async (p: { serviceId?: string; staffId?: string; date: string; timePref: string; name: string; phone?: string }) => {
    await apiService.joinBranchWaitlist(branchId!, p);
  };

  const goBack = () => navigate(`/customer/organizers/${organizerId}/branches/${branchId}`);

  return (
    <>
      <DashboardHeader />
      <div style={styles.page}>
        <ErrorBoundary>
          <div style={styles.shell}>
            <button style={styles.back} onClick={goBack}><ArrowLeft size={16} /> Back to branch</button>
            {phase === 'loading' ? (
              <div style={styles.center}>
                <div style={styles.splashMark}><Scissors size={28} color="#fff" /></div>
                <div style={styles.muted}>Getting things ready…</div>
              </div>
            ) : phase === 'error' ? (
              <div style={styles.center}>
                <div style={styles.stateIcon}><RefreshCw size={26} color="#b91c1c" /></div>
                <div style={styles.centerTitle}>Can’t load appointments</div>
                <div style={styles.muted}>{message}</div>
              </div>
            ) : (
              <SalonBookingWizard branchName={branchName} currency={currency} services={services} staff={staff} loadSlots={loadSlots} submit={submit} joinWaitlist={joinWaitlist} stickyFooter={false} confirmNote="— you’ll see it in your bookings" />
            )}
          </div>
        </ErrorBoundary>
      </div>
      <Footer />
    </>
  );
};

const SERIF = "'Fraunces','Iowan Old Style','Palatino Linotype','Palatino','Georgia',serif";
const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '70vh', background: '#efe7dc', display: 'flex', justifyContent: 'center', padding: '16px 0 28px' },
  shell: { width: 'min(500px, 100%)', background: '#f6f1ea', borderRadius: 18, boxShadow: '0 0 40px rgba(33,27,24,0.08)', padding: '14px 18px 18px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column' },
  back: { display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid #e3d8c8', color: '#211b18', borderRadius: 999, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer', alignSelf: 'flex-start', marginBottom: 6 },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center', padding: '48px 24px' },
  splashMark: { width: 64, height: 64, borderRadius: 20, background: 'linear-gradient(135deg,#a56a3c,#c79a5b)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 30px rgba(165,106,60,0.36)' },
  stateIcon: { width: 60, height: 60, borderRadius: '50%', background: 'rgba(185,28,28,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  centerTitle: { fontFamily: SERIF, fontSize: '1.3rem', fontWeight: 600, color: '#211b18' },
  muted: { color: '#8a7d6f', fontSize: '0.95rem', lineHeight: 1.5 },
};

export default BookAppointment;
